// The pieces every Books page shares. A statement is a FACE, not a data table: section headings,
// lines under them, subtotals and totals that are the engine's own figures. `StatementTable` draws
// that face from rows a page hands it, on the design system's table primitives. It formats; it
// never adds.

import type { ReactNode } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import {
  Card,
  Icon,
  PageHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { money } from './books-format';
import type { BooksWords } from './books-words';

export function BooksPageShell({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-24 p-24"
      {...testIdProp(testId)}
    >
      <PageHeader title={title} subtitle={description} />
      {children}
    </div>
  );
}

/** One figure, exactly as served, in tabular numerals. */
export function Money({ cents, className }: { cents: number; className?: string }) {
  return <span className={cn('tabular-nums', className)}>{money(cents)}</span>;
}

/** A period's lifecycle status in its served words, with a lock on a sealed or reopened month. */
export function StatusLabel({
  status,
  words,
  className,
}: {
  status: string;
  words: BooksWords;
  className?: string;
}) {
  const icon = status === 'locked' ? Lock : status === 'reopened' ? LockOpen : null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-6 font-sans text-s13 tracking-givmo',
        status === 'locked' ? 'text-text-secondary' : 'text-text',
        className,
      )}
    >
      {icon ? <Icon icon={icon} size={13} aria-hidden className="text-text-tertiary" /> : null}
      {words.status(status).label}
    </span>
  );
}

/** A calm, page-level statement of a state: a refusal, or an empty truth. */
export function BooksStateCard({
  title,
  description,
  action,
  testId,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  testId: string;
}) {
  return (
    <Card className="flex flex-col items-start gap-8 px-24 py-24" testId={testId}>
      <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">{title}</h2>
      {description ? (
        <p className="max-w-[640px] font-sans text-s14 tracking-givmo text-text-secondary">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-8">{action}</div> : null}
    </Card>
  );
}

/** A quiet "loading" line inside a card. */
export function BooksLoading({ children }: { children: ReactNode }) {
  return (
    <Card className="px-24 py-24">
      <p role="status" className="font-sans text-s13 text-text-secondary">
        {children}
      </p>
    </Card>
  );
}

// ── The statement face ───────────────────────────────────────────────────────

export type FaceRow =
  | { kind: 'heading'; label: string; key?: string; action?: ReactNode }
  | {
      kind: 'line';
      label: ReactNode;
      cells: ReactNode[];
      indent?: 0 | 1 | 2 | 3;
      key?: string;
      muted?: boolean;
      note?: ReactNode;
    }
  | { kind: 'subtotal'; label: ReactNode; cells: ReactNode[]; key?: string }
  | { kind: 'total'; label: ReactNode; cells: ReactNode[]; key?: string }
  | { kind: 'spacer'; key?: string }
  | { kind: 'detail'; content: ReactNode; key?: string };

export interface FaceColumn {
  label: ReactNode;
  /** The width of a figure column; the label column takes the rest. */
  width?: string;
}

export function StatementTable({
  columns,
  rows,
  testId,
  firstColumnLabel = '',
  narrow = false,
}: {
  columns: FaceColumn[];
  rows: FaceRow[];
  testId?: string;
  firstColumnLabel?: string;
  /** A one-figure face reads best in a column the eye can cross: label and amount stay close. */
  narrow?: boolean;
}) {
  return (
    <Table
      testId={testId}
      className="table-fixed"
      wrapperClassName={cn('w-full', narrow && 'max-w-[760px]')}
    >
      <colgroup>
        <col />
        {columns.map((column, index) => (
          <col key={index} style={{ width: column.width ?? '168px' }} />
        ))}
      </colgroup>
      {columns.length > 1 || firstColumnLabel !== '' ? (
        <THead>
          <tr>
            <TH className="h-[44px] whitespace-normal align-bottom">{firstColumnLabel}</TH>
            {columns.map((column, index) => (
              <TH
                key={index}
                align="right"
                className="h-[44px] whitespace-normal align-bottom leading-[1.35]"
              >
                {column.label}
              </TH>
            ))}
          </tr>
        </THead>
      ) : null}
      <TBody>
        {rows.map((row, index) => {
          const key = row.key ?? `${row.kind}-${index}`;
          if (row.kind === 'spacer') {
            return (
              <tr key={key} aria-hidden>
                <td colSpan={columns.length + 1} className="h-[12px]" />
              </tr>
            );
          }
          if (row.kind === 'detail') {
            return (
              <tr key={key}>
                <td colSpan={columns.length + 1} className="px-16 pb-12 pt-4">
                  {row.content}
                </td>
              </tr>
            );
          }
          if (row.kind === 'heading') {
            return (
              <tr key={key} className="border-b border-border-subtle">
                <th
                  scope="rowgroup"
                  colSpan={columns.length + 1}
                  className="h-[40px] px-16 pt-12 text-left align-bottom font-sans text-s13 font-w600 tracking-givmo text-text-strong"
                >
                  <span className="flex items-baseline justify-between gap-12">
                    <span>{row.label}</span>
                    {row.action}
                  </span>
                </th>
              </tr>
            );
          }
          if (row.kind === 'line') {
            return (
              <TR key={key} className="border-b-0">
                <TD
                  className={cn(
                    'h-[36px] py-6 text-s14',
                    row.indent === 3
                      ? 'pl-[64px]'
                      : row.indent === 2
                        ? 'pl-48'
                        : row.indent === 1
                          ? 'pl-32'
                          : 'pl-16',
                    row.muted ? 'text-text-secondary' : 'text-text',
                  )}
                >
                  <span className="inline-flex flex-wrap items-baseline gap-x-8">
                    <span>{row.label}</span>
                    {row.note}
                  </span>
                </TD>
                {row.cells.map((cell, cellIndex) => (
                  <TD
                    key={cellIndex}
                    align="right"
                    numeric
                    className={cn(
                      'h-[36px] py-6 text-s14',
                      row.muted ? 'text-text-secondary' : 'text-text',
                    )}
                  >
                    {cell}
                  </TD>
                ))}
              </TR>
            );
          }
          const strong = row.kind === 'total';
          return (
            <tr
              key={key}
              className={cn(
                'border-t border-border',
                strong ? 'border-b-2 border-b-border-strong' : 'border-b border-border-subtle',
              )}
            >
              <th
                scope="row"
                className={cn(
                  'h-[40px] px-16 text-left font-sans text-s14 tracking-givmo',
                  strong ? 'font-w600 text-text-strong' : 'font-w500 text-text',
                )}
              >
                {row.label}
              </th>
              {row.cells.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    'h-[40px] px-16 text-right font-sans text-s14 tabular-nums tracking-givmo',
                    strong ? 'font-w600 text-text-strong' : 'font-w500 text-text',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          );
        })}
      </TBody>
    </Table>
  );
}

/** The quiet line under a statement naming the chain position its figures were folded at. */
export function SourceLine({ children }: { children: ReactNode }) {
  return <p className="px-4 font-sans text-s12 tracking-givmo text-text-tertiary">{children}</p>;
}
